import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TriangleLogo } from "@/components/TriangleLogo";
import { LegalLinks } from "@/components/shared/LegalLinks";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  ArrowClockwise,
  Clock,
  PlayCircle,
  SignOut as LogOut,
} from "@phosphor-icons/react";

export default function PendingAccess() {
  usePageTitle("Acesso Pendente");
  const { user, signOut } = useCurrentUser();
  const queryClient = useQueryClient();
  const [isRechecking, setIsRechecking] = useState(false);

  const handleRecheck = async () => {
    if (isRechecking) return;
    setIsRechecking(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["profile-plan"] });
      // Hard reload guarantees PlanGate re-evaluates and routes user out if liberated
      window.location.reload();
    } finally {
      // Only reached if reload is blocked
      setIsRechecking(false);
    }
  };

  return (
    <main
      role="main"
      className="flex min-h-screen items-center justify-center bg-background bg-glow py-8"
    >
      <div className="text-center space-y-6 max-w-md px-6">
        <TriangleLogo size="xl" animate className="mx-auto opacity-50" />

        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
            <Clock className="w-5 h-5" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">
            Acesso ainda não foi liberado
          </h1>
          <p className="text-muted-foreground">
            Sua conta existe, mas seu acesso ainda não foi liberado.
          </p>
        </div>

        {user?.email && (
          <p className="text-sm text-muted-foreground bg-muted/40 rounded-lg px-4 py-2">
            {user.email}
          </p>
        )}

        <div
          className="rounded-lg border border-border/60 bg-card/60 px-4 py-3 text-left text-sm text-muted-foreground"
          aria-live="polite"
        >
          <p className="font-medium text-foreground mb-1">Aguarde a liberação</p>
          <p>
            Use o canal pelo qual recebeu seu convite para acompanhar a liberação do acesso.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            className="gap-2"
            onClick={handleRecheck}
            disabled={isRechecking}
            aria-label="Verificar novamente o status do acesso"
          >
            <ArrowClockwise
              className={`w-4 h-4 ${isRechecking ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            {isRechecking ? "Verificando..." : "Verificar novamente"}
          </Button>

          <Button
            variant="ghost"
            className="gap-2"
            disabled
            aria-label="Tour da plataforma — em breve"
            title="Em breve"
          >
            <PlayCircle className="w-4 h-4" aria-hidden="true" />
            Em breve: tour da plataforma
          </Button>
          <p className="text-xs text-muted-foreground/80">
            Vamos disponibilizar um tour curto enquanto seu acesso é liberado.
          </p>
        </div>

        <Button
          variant="ghost"
          className="gap-2"
          onClick={() => signOut()}
        >
          <LogOut className="w-4 h-4" aria-hidden="true" />
          Sair
        </Button>
        <LegalLinks />
      </div>
    </main>
  );
}
